import { isValidLength } from './planUtils';
import { VossProductGroupTagType } from '../../../Constants/VOSS/vossProductGroupTagType';
import { VossProductGroupType } from '../../../Constants/VOSS/vossProductGroupType';
import { VossRevenueRecognitionBehaviour } from '../../../Constants/VOSS/vossRevenueRecognitionBehaviour';
import { VossTierTagType } from '../../../Constants/VOSS/vossTierTagType';
import type {
    CreatePackageTierRequest,
    PackageProductGroup,
    PackageTier,
    ProductGroupDraft,
    ProductGroupRequest,
    ProductGroupTagDraft,
    TierDraft,
    TierRankChange,
    TierSaveStep,
    TierTagDraft,
    UpdatePackageTierRequest,
} from './Packages.types';

const compareNullable = (a: number | null | undefined, b: number | null | undefined) => (a ?? Number.MAX_SAFE_INTEGER) - (b ?? Number.MAX_SAFE_INTEGER);

export const sortTiersByRank = (tiers: PackageTier[]): PackageTier[] => [...tiers].sort((a, b) => compareNullable(a.rank, b.rank));

export const sortProductGroups = (groups: PackageProductGroup[]): PackageProductGroup[] => [...groups].sort((a, b) => compareNullable(a.sortOrder, b.sortOrder));

export const tierProductCount = (tier: PackageTier): number => (tier.productGroups ?? []).reduce((sum, group) => sum + (group.products ?? []).length, 0);

export const distinctTagValues = (tags: { tag?: string | null }[]): string[] => [...new Set(tags.map((tag) => tag.tag ?? '').filter((value) => value !== ''))];

// hands the existing rank values out in the new order instead of renumbering, so gapped ranks (10, 20, 30) survive a move
export const reorderTiers = (sortedTiers: PackageTier[], fromIndex: number, toIndex: number): TierRankChange[] => {
    const ranks = sortedTiers.map((tier, index) => tier.rank ?? index + 1);
    const reordered = [...sortedTiers];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);

    return reordered.map((tier, index) => ({ tier, rank: ranks[index] })).filter(({ tier, rank }) => tier.rank !== rank);
};

const nextAfter = (values: (number | null | undefined)[]): number => (values.length === 0 ? 1 : Math.max(...values.map((value) => value ?? 0)) + 1);

export const nextTierRank = (tiers: PackageTier[]): number => nextAfter(tiers.map((tier) => tier.rank));

export const nextSortOrder = (groups: ProductGroupDraft[]): number => nextAfter(groups.map((group) => group.sortOrder));

export const emptyTierDraft = (rank: number): TierDraft => ({ name: '', externalId: '', rank, restrictedChangeOperations: [], tags: [], productGroups: [] });

export const emptyTierTag = (): TierTagDraft => ({ tag: '', type: VossTierTagType.Merge, rank: 1 });

export const emptyProductGroupTag = (): ProductGroupTagDraft => ({ tag: '', type: VossProductGroupTagType.Migration, rank: null });

export const emptyProductGroupDraft = (sortOrder: number): ProductGroupDraft => ({
    key: crypto.randomUUID(),
    name: '',
    externalId: '',
    productGroupType: VossProductGroupType.Included,
    sortOrder,
    revenueRecognitionBehaviour: VossRevenueRecognitionBehaviour.Normal,
    tags: [],
    productIds: [],
});

// a rank typed before switching to a type without one stays in the draft, so it's neither sent nor validated; VOSS ignores the rank of migration tags
export const tierTagTakesRank = (type: VossTierTagType): boolean => type !== VossTierTagType.Exclusivity;
export const groupTagTakesRank = (type: VossProductGroupTagType): boolean => type === VossProductGroupTagType.Exclusive;

export const parseNumberInput = (value: string): number | null => (value === '' ? null : Number(value));

export const trimmedOrUndefined = (value: string): string | undefined => (value.trim().length === 0 ? undefined : value.trim());

export const toProductGroupRequest = (group: ProductGroupDraft): ProductGroupRequest => ({
    name: group.name.trim(),
    externalId: trimmedOrUndefined(group.externalId),
    productGroupType: group.productGroupType,
    type: group.type,
    sortOrder: group.sortOrder ?? undefined,
    revenueRecognitionBehaviour: group.revenueRecognitionBehaviour,
    tags: group.tags.map((tag) => ({ tag: tag.tag.trim(), type: tag.type, rank: groupTagTakesRank(tag.type) ? (tag.rank ?? undefined) : undefined })),
    products: group.productIds.map((id) => ({ id })),
});

export const toUpdateTierRequest = (draft: TierDraft): UpdatePackageTierRequest => ({
    name: draft.name.trim(),
    externalId: trimmedOrUndefined(draft.externalId),
    rank: draft.rank ?? undefined,
    type: draft.type,
    restrictedChangeOperations: draft.restrictedChangeOperations,
    tags: draft.tags.map((tag) => ({ tag: tag.tag.trim(), type: tag.type, rank: tierTagTakesRank(tag.type) ? (tag.rank ?? undefined) : undefined })),
});

export const toCreateTierRequest = (draft: TierDraft): CreatePackageTierRequest => ({
    ...toUpdateTierRequest(draft),
    productGroups: draft.productGroups.map(toProductGroupRequest),
});

const toProductGroupDraft = (group: PackageProductGroup): ProductGroupDraft => ({
    key: group.id ?? crypto.randomUUID(),
    id: group.id,
    type: group.type,
    name: group.name ?? '',
    externalId: group.externalId ?? '',
    productGroupType: group.productGroupType ?? VossProductGroupType.Included,
    sortOrder: group.sortOrder ?? null,
    revenueRecognitionBehaviour: group.revenueRecognitionBehaviour ?? VossRevenueRecognitionBehaviour.Normal,
    tags: (group.tags ?? []).map((tag) => ({ tag: tag.tag ?? '', type: tag.type ?? VossProductGroupTagType.Migration, rank: tag.rank ?? null })),
    productIds: (group.products ?? []).flatMap((product) => (product.id === undefined ? [] : [product.id])),
});

export const toTierDraft = (tier: PackageTier): TierDraft => ({
    type: tier.type,
    name: tier.name ?? '',
    externalId: tier.externalId ?? '',
    rank: tier.rank ?? null,
    restrictedChangeOperations: tier.restrictedChangeOperations ?? [],
    tags: (tier.tags ?? []).map((tag) => ({ tag: tag.tag ?? '', type: tag.type ?? VossTierTagType.Merge, rank: tag.rank ?? null })),
    productGroups: sortProductGroups(tier.productGroups ?? []).map(toProductGroupDraft),
});

const isSameRequest = (a: object, b: object): boolean => JSON.stringify(a) === JSON.stringify(b);

// only what changed is sent, so a version past Draft never gets a write for a field VOSS would reject; creates go last
export const planTierSave = (tier: PackageTier, draft: TierDraft): TierSaveStep[] => {
    const original = toTierDraft(tier);
    const originalGroups = new Map(original.productGroups.map((group) => [group.id, group]));
    const keptIds = new Set(draft.productGroups.map((group) => group.id));
    const tierBody = toUpdateTierRequest(draft);

    return [
        ...(isSameRequest(tierBody, toUpdateTierRequest(original)) ? [] : [{ kind: 'updateTier' as const, body: tierBody }]),
        ...draft.productGroups.flatMap((group) => {
            const before = group.id === undefined ? undefined : originalGroups.get(group.id);
            const body = toProductGroupRequest(group);

            return group.id === undefined || before === undefined || isSameRequest(body, toProductGroupRequest(before))
                ? []
                : [{ kind: 'updateGroup' as const, groupId: group.id, body }];
        }),
        ...original.productGroups.flatMap((group) => (group.id === undefined || keptIds.has(group.id) ? [] : [{ kind: 'deleteGroup' as const, groupId: group.id }])),
        ...draft.productGroups.flatMap((group) => (group.id === undefined ? [{ kind: 'createGroup' as const, body: toProductGroupRequest(group) }] : [])),
    ];
};

export const isOptionalWholeNumber = (value: number | null): boolean => value === null || isValidLength(value);

export const isValidTierDraft = (draft: TierDraft): boolean =>
    draft.name.trim().length > 0 &&
    draft.rank !== null &&
    isValidLength(draft.rank) &&
    draft.tags.every((tag) => tag.tag.trim().length > 0 && (!tierTagTakesRank(tag.type) || isOptionalWholeNumber(tag.rank))) &&
    draft.productGroups.every(
        (group) =>
            group.name.trim().length > 0 &&
            isOptionalWholeNumber(group.sortOrder) &&
            group.tags.every((tag) => tag.tag.trim().length > 0 && (!groupTagTakesRank(tag.type) || isOptionalWholeNumber(tag.rank)))
    );
